import { useEffect, useState, useCallback, useMemo, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useMemberspotTraining from '../../hooks/useMemberspotTraining';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const STATUS_LABELS = {
  pending: 'Geplant',
  invited: 'Eingeladen',
  inProgress: 'In Bearbeitung',
  completed: 'Abgeschlossen',
  overdue: 'Überfällig',
  failedSync: 'Sync-Fehler',
  archived: 'Archiviert',
};

const STATUS_COLOR = {
  pending: 'default',
  invited: 'info',
  inProgress: 'primary',
  completed: 'success',
  overdue: 'error',
  failedSync: 'warning',
  archived: 'default',
};

function formatDeadlineCountdown(dueAt) {
  if (!dueAt) return '';
  const days = dayjs(dueAt).diff(dayjs(), 'day');
  if (days > 1) return `noch ${days} Tage`;
  if (days === 1) return 'noch 1 Tag';
  if (days === 0) return 'heute fällig';
  return `${Math.abs(days)} Tage überfällig`;
}

export default function MemberspotTrainingStatus() {
  const authCtx = useContext(AuthContext);
  const canReadAny =
    authCtx.checkRight('memberspotTraining:read:own') ||
    authCtx.checkRight('memberspotTraining:read');
  const canSync = authCtx.checkRight('memberspotTraining:special:sync');
  const canAcknowledge = authCtx.checkRight('memberspotTraining:special:acknowledge:own');
  const canManageConsequences = authCtx.checkRight('memberspotTraining:special:manageConsequences');
  const canOpenLink = authCtx.checkRight('memberspotTraining:special:openLink');

  const ms = useMemberspotTraining();

  const [assignments, setAssignments] = useState([]);
  const [hasGlobalRead, setHasGlobalRead] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [ackOpen, setAckOpen] = useState(false);
  const [ackAssignment, setAckAssignment] = useState(null);
  const [ackNote, setAckNote] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await ms.listAssignments();
      setAssignments(res?.assignments || []);
      setHasGlobalRead(!!res?.hasGlobalRead);
    } finally {
      setInitialLoading(false);
    }
  }, [ms]);

  useEffect(() => {
    if (!canReadAny) return;
    load();
  }, [canReadAny]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return assignments.filter(a => {
      if (statusFilter === 'open' && !['invited', 'inProgress', 'overdue'].includes(a.status)) return false;
      if (statusFilter === 'overdue' && a.status !== 'overdue') return false;
      if (statusFilter === 'completed' && a.status !== 'completed') return false;
      if (statusFilter === 'failed' && a.status !== 'failedSync') return false;
      if (statusFilter === 'all') {
        // no extra filter
      }
      if (!term) return true;
      const name = `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.toLowerCase();
      const email = (a.user?.email || a.emailSnapshot || '').toLowerCase();
      const rule = (a.ruleNameSnapshot || '').toLowerCase();
      return name.includes(term) || email.includes(term) || rule.includes(term);
    });
  }, [assignments, statusFilter, searchTerm]);

  const stats = useMemo(() => {
    const out = { open: 0, dueSoon: 0, overdue: 0, completed: 0, failed: 0 };
    const sevenDays = dayjs().add(7, 'day');
    for (const a of assignments) {
      if (a.status === 'completed') out.completed += 1;
      if (a.status === 'overdue') out.overdue += 1;
      if (a.status === 'failedSync') out.failed += 1;
      if (['invited', 'inProgress', 'overdue'].includes(a.status)) {
        out.open += 1;
        if (a.dueAt && dayjs(a.dueAt).isBefore(sevenDays) && a.status !== 'overdue') {
          out.dueSoon += 1;
        }
      }
    }
    return out;
  }, [assignments]);

  if (!canReadAny) return <Navigate to='/welcome' replace />;

  const handleSync = async assignmentId => {
    setBusyId(assignmentId);
    try {
      await ms.syncAssignment(assignmentId);
      await load();
      toast.success('Schulung synchronisiert');
    } finally {
      setBusyId(null);
    }
  };

  const handleOpenLink = async assignmentId => {
    setBusyId(assignmentId);
    try {
      const res = await ms.openMemberspotLink(assignmentId);
      if (res?.loginUrl) window.open(res.loginUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setBusyId(null);
    }
  };

  const openAck = assignment => {
    setAckAssignment(assignment);
    setAckNote('');
    setAckOpen(true);
  };

  const submitAck = async () => {
    setBusyId(ackAssignment._id);
    try {
      await ms.acknowledgeConversation(ackAssignment._id, ackNote);
      toast.success('Gespräch dokumentiert');
      setAckOpen(false);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleDeactivate = async (assignmentId, action) => {
    setBusyId(assignmentId);
    try {
      await ms.runConsequence(assignmentId, action);
      toast.success(action === 'deactivateMemberspot' ? 'Memberspot deaktiviert' : 'Memberspot reaktiviert');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 3, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant='h4' sx={{ fontWeight: 600, mb: 1 }}>
        Schulungsstand
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        {hasGlobalRead
          ? 'Du siehst den Schulungsstand aller Mitarbeiter.'
          : 'Du siehst den Schulungsstand der Mitarbeiter deiner zugewiesenen Standorte.'}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='overline'>Offen</Typography>
              <Typography variant='h5'>{stats.open}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='overline'>Bald fällig</Typography>
              <Typography variant='h5'>{stats.dueSoon}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card variant='outlined' sx={{ borderColor: 'error.main' }}>
            <CardContent>
              <Typography variant='overline' color='error.main'>
                Überfällig
              </Typography>
              <Typography variant='h5' color='error.main'>
                {stats.overdue}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='overline'>Abgeschlossen</Typography>
              <Typography variant='h5'>{stats.completed}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='overline'>Sync-Fehler</Typography>
              <Typography variant='h5'>{stats.failed}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Stack direction='row' spacing={2} sx={{ mb: 2 }} alignItems='center' flexWrap='wrap'>
        <TextField
          select
          label='Status'
          size='small'
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value='open'>Offen (inkl. überfällig)</MenuItem>
          <MenuItem value='overdue'>Nur überfällig</MenuItem>
          <MenuItem value='completed'>Abgeschlossen</MenuItem>
          <MenuItem value='failed'>Sync-Fehler</MenuItem>
          <MenuItem value='all'>Alle</MenuItem>
        </TextField>
        <TextField
          size='small'
          label='Suche'
          placeholder='Name, E-Mail oder Schulung'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280 }}
        />
      </Stack>

      {initialLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Alert severity='info'>Keine Schulungszuweisungen passen zu den Filtern.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map(a => {
            const needsAck =
              a.requiresAcknowledgement &&
              ['invited', 'inProgress', 'overdue'].includes(a.status) &&
              a.dueAt &&
              dayjs(a.dueAt).diff(dayjs(), 'day') <= 7 &&
              !a.managerConversation?.acknowledgedAt;
            const showAckButton = needsAck && canAcknowledge && a.canAcknowledge;
            return (
              <Paper
                key={a._id}
                sx={{
                  p: 2,
                  borderLeft: theme =>
                    `4px solid ${
                      a.status === 'overdue'
                        ? theme.palette.error.main
                        : a.status === 'completed'
                        ? theme.palette.success.main
                        : theme.palette.divider
                    }`,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ md: 'center' }}
                  justifyContent='space-between'
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                      <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                        {[a.user?.firstName, a.user?.lastName].filter(Boolean).join(' ') ||
                          a.user?.username ||
                          a.emailSnapshot}
                      </Typography>
                      <Chip
                        label={STATUS_LABELS[a.status] || a.status}
                        size='small'
                        color={STATUS_COLOR[a.status] || 'default'}
                      />
                      {a.managerConversation?.acknowledgedAt && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={`Gespräch ${dayjs(a.managerConversation.acknowledgedAt).format('DD.MM.YYYY')}`}
                          size='small'
                          color='success'
                          variant='outlined'
                        />
                      )}
                      {a.consequenceState?.memberspotAccessDeactivatedAt && (
                        <Chip
                          label='Memberspot deaktiviert'
                          size='small'
                          color='warning'
                          variant='outlined'
                        />
                      )}
                    </Stack>
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                      {a.ruleNameSnapshot}
                      {a.user?.email ? ` · ${a.user.email}` : ''}
                      {a.user?.branchNames?.length ? ` · ${a.user.branchNames.join(', ')}` : ''}
                    </Typography>
                    {a.dueAt && (
                      <Typography
                        variant='caption'
                        color={a.status === 'overdue' ? 'error.main' : 'text.secondary'}
                      >
                        Frist {dayjs(a.dueAt).format('DD.MM.YYYY')} · {formatDeadlineCountdown(a.dueAt)}
                      </Typography>
                    )}
                    {a.lastSyncError?.message && (
                      <Typography variant='caption' color='warning.main' sx={{ display: 'block' }}>
                        Sync-Fehler: {a.lastSyncError.message}
                      </Typography>
                    )}
                    {a.courseProgress?.length > 0 && (
                      <Box sx={{ mt: 1, maxWidth: 420 }}>
                        {a.courseProgress.map(cp => (
                          <Box key={cp.courseId} sx={{ mb: 0.5 }}>
                            <Typography variant='caption' color='text.secondary'>
                              {cp.courseId}: {cp.completedPosts}/{cp.activePosts || '?'} Lektionen
                            </Typography>
                            <LinearProgress
                              variant='determinate'
                              value={
                                cp.activePosts > 0
                                  ? Math.min(100, (cp.completedPosts / cp.activePosts) * 100)
                                  : 0
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    {showAckButton && (
                      <Button
                        variant='contained'
                        color='warning'
                        size='small'
                        onClick={() => openAck(a)}
                      >
                        Gespräch geführt
                      </Button>
                    )}
                    {canOpenLink && (
                      <Tooltip title='In Memberspot öffnen'>
                        <span>
                          <IconButton
                            disabled={busyId === a._id}
                            onClick={() => handleOpenLink(a._id)}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canSync && (
                      <Tooltip title='Jetzt synchronisieren'>
                        <span>
                          <IconButton disabled={busyId === a._id} onClick={() => handleSync(a._id)}>
                            <SyncIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canManageConsequences && (
                      <Button
                        size='small'
                        variant='outlined'
                        color={a.consequenceState?.memberspotAccessDeactivatedAt ? 'success' : 'warning'}
                        disabled={busyId === a._id}
                        onClick={() =>
                          handleDeactivate(
                            a._id,
                            a.consequenceState?.memberspotAccessDeactivatedAt
                              ? 'reactivateMemberspot'
                              : 'deactivateMemberspot'
                          )
                        }
                      >
                        {a.consequenceState?.memberspotAccessDeactivatedAt
                          ? 'Reaktivieren'
                          : 'Memberspot sperren'}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Dialog open={ackOpen} onClose={() => setAckOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Mitarbeitergespräch dokumentieren</DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Bestätige, dass du das Gespräch mit dem Mitarbeiter zur Schulung geführt hast. Datum und
            verantwortliche Person werden automatisch gespeichert.
          </Typography>
          <TextField
            label='Notiz (optional)'
            value={ackNote}
            onChange={e => setAckNote(e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAckOpen(false)}>Abbrechen</Button>
          <Button variant='contained' color='success' onClick={submitAck} disabled={busyId === ackAssignment?._id}>
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
